import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '@app/components/common/Modal/Modal';
import { IDealRoomPeople } from '@app/interfaces/deal-room-people';
import { useTranslation } from 'react-i18next';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { List } from 'antd';
import { DealRoomUserItem } from '../DealRoomWhatsAppModal/DealRoomWhatsAppModal';
import { Input, TextArea } from '@app/components/common/inputs/Input/Input';
import { IDealRoom, IDealRoomShare } from '@app/interfaces/deal-room';
import * as S from '../DealRoomWhatsAppModal/DealRoomWhatsAppModal.styles';
import { getDealRoomFirstBuildingText, getPeopleFullName, getUserFullName } from '@app/utils/utils';
import { IUser } from '@app/interfaces/user';
import { IClient } from '@app/interfaces/client';

interface DealRoomEmailModalProps {
  dealRoom: IDealRoomShare;
  shortLink?: string | null;
  open: boolean;
  people: IDealRoomPeople[];
  selectedPeople?: string | null;
  onClose: () => void;
}

export const DealRoomEmailModal: React.FC<DealRoomEmailModalProps> = ({
  dealRoom,
  shortLink,
  open,
  people,
  selectedPeople,
  onClose,
}) => {
  const { t } = useTranslation();
  const [form] = BaseForm.useForm();

  const [previewMessage, setPreviewMessage] = useState('');
  const [key, setKey] = useState(0);

  const onOk = () => {
    form.submit();
  };

  const getEmails = (ids: string[]): string[] => {
    return people.filter((p) => ids.includes(p.id) && p.email).map((p) => p.email || '');
  };

  const onSubmit = (values: { to: string[]; cc: string[]; bcc: string[]; subject: string; message: string }) => {
    const { to, cc, bcc, subject } = values;
    const toEmails = getEmails(to || []);
    const ccEmails = getEmails(cc || []);
    const bccEmails = getEmails(bcc || []);
    let mailToLink = `mailto:${toEmails.join(',')}`;
    if (ccEmails.length > 0) {
      mailToLink += `?cc=${ccEmails.join(',')}`;
    }
    if (bccEmails.length > 0) {
      mailToLink += `${cc.length > 0 ? '&' : '?'}bcc=${bccEmails.join(',')}`;
    }
    if (subject) {
      mailToLink += `${cc.length > 0 || bcc.length > 0 ? '&' : '?'}subject=${encodeURIComponent(subject)}`;
    }
    if (previewMessage) {
      mailToLink += `${cc.length > 0 || bcc.length > 0 || subject ? '&' : '?'}body=${encodeURIComponent(
        previewMessage.replace(/<br\/>/g, '\n'),
      )}`;
    }
    window.open(mailToLink, '_blank');
    onClose();
  };

  const allFields = ['to', 'cc', 'bcc'];

  const toggleCheck = (field: string, id: string) => {
    const currentSelected: string[] = form.getFieldValue(field) || [];
    if (currentSelected.includes(id)) {
      form.setFieldsValue({ [field]: currentSelected.filter((x) => x !== id) });
    } else {
      form.setFieldsValue({ [field]: [...currentSelected, id] });
    }

    // if id then remove it else add it
    allFields
      .filter((f) => f !== field)
      .forEach((f) => {
        const otherSelected: string[] = form.getFieldValue(f) || [];
        if (otherSelected.includes(id)) {
          form.setFieldsValue({ [f]: otherSelected.filter((x) => x !== id) });
        }
      });

    calculatePreviewMessage();
  };

  const getChecked = (field: string, id: string): boolean => {
    const currentSelected: string[] = form.getFieldValue(field) || [];
    return currentSelected.includes(id);
  };

  const onSelectAll = (field: string) => {
    form.setFieldsValue({ [field]: people.map((p) => p.id).filter((p) => p) });

    // if id then remove it else add it
    allFields
      .filter((f) => f !== field)
      .forEach((f) => {
        form.setFieldsValue({ [f]: [] });
      });

    calculatePreviewMessage();
  };

  const onClearAll = (field: string) => {
    form.setFieldsValue({ [field]: [] });
    calculatePreviewMessage();
  };

  const calculatePreviewMessage = useCallback(() => {
    let message = '';
    const firstPeople = people.find((p) => (form.getFieldValue('to') || []).includes(p.id));
    if (firstPeople) {
      message += `${getPeopleFullName(firstPeople)} שלום,`;
    } else {
      message += `שלום,`;
    }
    if (dealRoom.responsible) {
      message += `<br/><br/>${getUserFullName(
        dealRoom.responsible as IUser,
      )} עדכן/נה את האזור האישי, ניתן להיכנס לאזור האישי בלינק הבא: ${shortLink || ''}`;
    }
    if (dealRoom.client && (dealRoom.client as IClient)?.id_num) {
      message += `<br/><br/>מספר לקוח: ${(dealRoom.client as IClient)?.id_num}`;
    }
    message += `<br/><br/>בברכה,`;
    message += `<br/>בר-עוז המרכז לביטוח`;
    setPreviewMessage(message);
    form.setFieldValue('message', message.replace(/<br\/>/g, '\n'));
    setKey((prev) => prev + 1);
  }, [dealRoom.client, dealRoom.responsible, form, people, shortLink]);

  useEffect(() => {
    if (selectedPeople) {
      form.setFieldsValue({ to: [selectedPeople], cc: [], bcc: [] });
    } else {
      form.setFieldsValue({ to: people.map((p) => p.id).filter((p) => p), cc: [], bcc: [] });
    }
    form.setFieldsValue({
      subject: `${(dealRoom.client as IClient)?.id_num || ''}: ${getDealRoomFirstBuildingText(
        dealRoom as IDealRoom,
      )} ,עדכון מידע`,
    });
  }, [dealRoom, form, people, selectedPeople]);

  useEffect(() => {
    calculatePreviewMessage();
  }, [calculatePreviewMessage]);

  return (
    <Modal
      size="large"
      open={open}
      title={t('dealRooms.landing.sendEmail')}
      onCancel={onClose}
      onOk={() => onOk()}
      okText={t('dealRooms.landing.send')}
      cancelText={t('common.cancel')}
      closable={true}
      maskClosable={false}
      destroyOnClose
    >
      <S.BaseFormWrapper
        form={form}
        layout="vertical"
        name="dealRoomEmailForm"
        onFinish={onSubmit}
        style={{ width: '100%' }}
        onValuesChange={(changes) => {
          if (changes.to || changes.cc || changes.bcc) {
            calculatePreviewMessage();
          }
          if (changes.message) {
            setPreviewMessage(changes.message.replace(/\n/g, '<br/>'));
            setKey((prev) => prev + 1);
          }
        }}
      >
        <BaseForm.Item
          name="to"
          rules={[{ required: true, message: t('dealRooms.landing.toRequired') }]}
          label={
            <S.FlexWrapper>
              <span>{t('dealRooms.landing.to')}</span>
              <S.ActionWrapper>
                <S.Link onClick={() => onSelectAll('to')}>{t('dealRooms.landing.selectAll')}</S.Link>|
                <S.Link onClick={() => onClearAll('to')}>{t('dealRooms.landing.clearAll')}</S.Link>
              </S.ActionWrapper>
            </S.FlexWrapper>
          }
        >
          <List
            dataSource={people.filter((p) => p.email)}
            grid={{ gutter: 16, column: 2 }}
            renderItem={(item) => (
              <DealRoomUserItem
                item={item}
                key={item.id}
                checked={getChecked('to', item.id)}
                toggleCheck={(id) => toggleCheck('to', id)}
                type="email"
              />
            )}
          />
        </BaseForm.Item>
        <BaseForm.Item
          name="subject"
          label={t('dealRooms.landing.subject')}
          rules={[{ required: true, message: t('dealRooms.landing.subjectRequired') }]}
        >
          <Input placeholder={t('dealRooms.landing.subject')} />
        </BaseForm.Item>
        <BaseForm.Item name="message" label={t('dealRooms.landing.message')}>
          <TextArea rows={4} placeholder={t('dealRooms.landing.message')} />
        </BaseForm.Item>
        <BaseForm.Item
          name="cc"
          label={
            <S.FlexWrapper>
              <span>{t('dealRooms.landing.cc')}</span>
              <S.ActionWrapper>
                <S.Link onClick={() => onSelectAll('cc')}>{t('dealRooms.landing.selectAll')}</S.Link>|
                <S.Link onClick={() => onClearAll('cc')}>{t('dealRooms.landing.clearAll')}</S.Link>
              </S.ActionWrapper>
            </S.FlexWrapper>
          }
        >
          <List
            dataSource={people.filter((p) => p.email)}
            grid={{ gutter: 16, column: 2 }}
            renderItem={(item) => (
              <DealRoomUserItem
                item={item}
                key={item.id}
                checked={getChecked('cc', item.id)}
                toggleCheck={(id) => toggleCheck('cc', id)}
                type="email"
              />
            )}
          />
        </BaseForm.Item>
        <BaseForm.Item
          name="bcc"
          label={
            <S.FlexWrapper>
              <span>{t('dealRooms.landing.bcc')}</span>
              <S.ActionWrapper>
                <S.Link onClick={() => onSelectAll('bcc')}>{t('dealRooms.landing.selectAll')}</S.Link>|
                <S.Link onClick={() => onClearAll('bcc')}>{t('dealRooms.landing.clearAll')}</S.Link>
              </S.ActionWrapper>
            </S.FlexWrapper>
          }
        >
          <List
            dataSource={people.filter((p) => p.email)}
            grid={{ gutter: 16, column: 2 }}
            renderItem={(item) => (
              <DealRoomUserItem
                item={item}
                key={item.id}
                checked={getChecked('bcc', item.id)}
                toggleCheck={(id) => toggleCheck('bcc', id)}
                type="email"
              />
            )}
          />
        </BaseForm.Item>
        <S.PreviewContainer key={key}>
          <S.PreviewWrapper>
            <S.PreviewHeader>{t('dealRooms.landing.emailMessagePreview')}</S.PreviewHeader>
          </S.PreviewWrapper>
          <S.PreviewWrapper>
            <S.PreviewTitle>{t('dealRooms.landing.subject')}</S.PreviewTitle>
            <S.PreviewDescription>{form.getFieldValue('subject')}</S.PreviewDescription>
          </S.PreviewWrapper>
          {form.getFieldValue('to')?.length > 0 && (
            <S.PreviewWrapper className="border-top">
              <S.PreviewTitle>{t('dealRooms.landing.to')}</S.PreviewTitle>
              <S.PreviewDescription>
                {people
                  .filter((p) => form.getFieldValue('to').includes(p.id))
                  .map((p) => p.email)
                  .join(', ')}
              </S.PreviewDescription>
            </S.PreviewWrapper>
          )}
          {form.getFieldValue('cc')?.length > 0 && (
            <S.PreviewWrapper className="border-top">
              <S.PreviewTitle>{t('dealRooms.landing.cc')}</S.PreviewTitle>
              <S.PreviewDescription>
                {people
                  .filter((p) => form.getFieldValue('cc').includes(p.id))
                  .map((p) => p.email)
                  .join(', ')}
              </S.PreviewDescription>
            </S.PreviewWrapper>
          )}
          {form.getFieldValue('bcc')?.length > 0 && (
            <S.PreviewWrapper className="border-top">
              <S.PreviewTitle>{t('dealRooms.landing.bcc')}</S.PreviewTitle>
              <S.PreviewDescription>
                {people
                  .filter((p) => form.getFieldValue('bcc').includes(p.id))
                  .map((p) => p.email)
                  .join(', ')}
              </S.PreviewDescription>
            </S.PreviewWrapper>
          )}
          <S.PreviewWrapper className="border-top">
            <S.PreviewTitle>{t('dealRooms.landing.content')}</S.PreviewTitle>
            <S.PreviewDescription dangerouslySetInnerHTML={{ __html: previewMessage }}></S.PreviewDescription>
          </S.PreviewWrapper>
        </S.PreviewContainer>
      </S.BaseFormWrapper>
    </Modal>
  );
};
